import {FormModel} from '@xh/hoist/cmp/form';
import {HoistModel, LoadSpec, managed, SelectOption, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {FullQuote, Order, OrderStatusEnum, Security} from '../../../../data/DataTypes';
import {required} from '@xh/hoist/data';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';

export class OrderTicketModel extends HoistModel {
    private timer;
    @observable accessor originalQuantity: number;
    @observable accessor originalTicker: string;
    @observable accessor isOpen: boolean;

    @observableRef accessor fullQuote: FullQuote;
    @managed formModel = this.createFormModel();

    constructor() {
        super();

        this.addReaction({
            track: () => this.formModel.values.orderType,
            run: () => {
                this.formModel.fields.limitPrice.setDisabled(!this.isLimitOrder);
            }
        });

        this.addReaction({
            track: () => this.formModel.values.ticker,
            run: () => {
                clearTimeout(this.timer);
                this.reset();
                this.loadAsync();
            }
        });

        this.addReaction({
            track: () => [
                this.formModel.values.action,
                this.formModel.values.orderType,
                this.formModel.values.action,
                this.formModel.values.quantity,
                this.fullQuote
            ],
            run: () => {
                const {quantity, action} = this.formModel.values,
                    price = this.executionPrice,
                    estAmount = price * quantity * (action == 'buy' || action == 'buyToCover' ? -1 : 1);

                this.formModel.setValues({estAmount: isNaN(estAmount) ? 0 : estAmount});
            }
        });
    }

    @action
    async open(portfolioId, ticker: string, action: string, quantity: number) {
        this.originalTicker = ticker;
        this.originalQuantity = quantity;

        this.formModel.init({portfolioId, ticker, action});
        this.fullQuote = null;
        this.isOpen = true;
        await this.loadAsync();
        if (!this.fullQuote) this.fullQuote = {isListed: true} as FullQuote; // avoid null checks in UI
        this.isOpen = true;
    }

    @action close() {
        clearTimeout(this.timer);
        this.isOpen = false;
    }

    cancel() {
        this.close();
    }

    sellAll() {
        if (this.originalQuantity) this.formModel.setValues({quantity: this.originalQuantity, action: 'sell'});
    }

    onSecuritySelect(security: Security) {
        this.formModel.setValues({ticker: security.ticker});
    }

    async place() {
        const data = this.formModel.getData(),
            order: Order = {
                id: 0,
                orderStatus: OrderStatusEnum.Open,
                orderTiming: data.timing,
                orderType: data.orderType,
                portfolioId: data.portfolioId,
                executionPrice: this.executionPrice,
                quantity: data.quantity,
                ticker: data.ticker,
                transactionType: data.action
            };
        const confirm = await XH.fetchService
            .postJson({
                url: `${XH.getConf('svcApiUrl')}/orders`,
                body: order,
                ...getDefaultOptions()
            })
            .catchDefault({alertType: 'toast'});
        this.close();
        if (confirm) XH.successToast(confirm.confirmationMessage);
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (!this.isOpen) return;

        const ticker = this.formModel.values.ticker;
        if (!ticker) return;

        const quote = await XH.fetchService
            .getJson({
                url: `${XH.getConf('svcApiUrl')}/quote/fullquote/${ticker}`,
                ...getDefaultOptions()
            })
            .catchDefault({alertType: 'toast'});

        runInAction(() => (this.fullQuote = quote));
        this.timer = setTimeout(() => this.loadAsync(), 1000);
    }

    get isLimitOrder(): boolean {
        return this.formModel.fields.orderType.value !== 'market';
    }

    get isLong(): boolean {
        const action = this.formModel.fields.action.value;
        return action == 'buy' || action == 'buyToCover';
    }

    get price(): number {
        if (!this.fullQuote) return null;
        return (this.fullQuote.bid + this.fullQuote.ask) / 2;
    }

    get executionPrice(): number {
        const {limitPrice} = this.formModel.values;
        if (this.isLimitOrder) return limitPrice;
        return this.isLong ? this.fullQuote?.ask : this.fullQuote?.bid;
    }

    get orderTypes(): SelectOption[] {
        return [
            {label: 'Limit', value: 'limit'},
            {label: 'Market', value: 'market'},
            {label: 'Stop Limit', value: 'stopLimit'},
            {label: 'Stop Market', value: 'stopMarket'}
        ];
    }

    private reset() {
        // keep portfolioId
        if (this.formModel.values.ticker != this.originalTicker) this.originalQuantity = null;

        this.formModel.setValues({
            action: 'buy',
            quantity: null,
            orderType: 'market',
            limitPrice: null,
            timing: 'day'
        });
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [
                {name: 'portfolioId'},
                {name: 'ticker', rules: [required]},
                {name: 'action', initialValue: 'buy', rules: [required]},
                {name: 'quantity', rules: [required]},
                {name: 'orderType', initialValue: 'market', rules: [required]},
                {name: 'limitPrice'},
                {name: 'timing', initialValue: 'day', rules: [required]},
                {name: 'estAmount', disabled: true}
            ]
        });
    }
}
